import React, { useState } from "react";
import {
  ChevronLeft, ChevronRight, ChevronDown, Settings2, BadgeCheck,
  Zap, Building2, MapPin, Briefcase, Users, Layers, Clock, CheckCircle2, ExternalLink,
} from "lucide-react";
import { CandidateProfile } from "../../../Candidate/Types/CandidateType";
import { DEFAULT_RESULT_COLUMNS, OPTIONAL_RESULT_COLUMNS } from "../Types/TalentIntelligenceTypes";

interface CandidateResultTableProps {
  results: CandidateProfile[];
  totalElements: number;
  page: number;
  totalPages: number;
  loading: boolean;
  onPageChange: (page: number) => void;
  columns: string[];
  onColumnsChange: (columns: string[]) => void;
  onOpenDetails?: (candidate: CandidateProfile) => void;
}

const initials = (c: CandidateProfile) =>
  `${c.firstName?.[0] || ""}${c.lastName?.[0] || ""}`.toUpperCase() || "?";

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

const MatchScorer: React.FC<{ candidate: CandidateProfile }> = ({ candidate }) => {
  const overall = candidate.overallScore;
  if (overall === null || overall === undefined) return <span className="text-xs text-slate-300">—</span>;
  const pct = Math.min(100, Math.round((Number(overall) / 10) * 100));
  const color =
    pct >= 85 ? "bg-emerald-500" : pct >= 70 ? "bg-indigo-500" : pct >= 50 ? "bg-amber-500" : "bg-slate-400";
  return (
    <span className="group relative inline-flex flex-col items-start gap-1" title="Why this candidate matches">
      <span className="inline-flex items-center gap-1.5">
        <span className="text-sm font-black text-slate-800">{pct}%</span>
        <span className="text-[9px] text-slate-400 font-semibold uppercase">Match</span>
      </span>
      <span className="w-14 h-1 rounded-full bg-slate-100 overflow-hidden">
        <span className={`block h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
      </span>
      <span className="hidden group-hover:block absolute z-20 top-full mt-1 left-0 w-44 bg-slate-900 text-white text-[10px] p-2 rounded-lg shadow-xl">
        <p className="font-bold mb-1">Why this candidate</p>
        <ul className="space-y-0.5 opacity-90">
          <li>• {(candidate.totalYearsOfExperience ?? 0)} yrs experience</li>
          <li>• Leadership score {candidate.leadershipScore ?? "—"}/10</li>
          <li>• Business impact {candidate.businessImpactScore ?? "—"}/10</li>
          <li>• Achievement {(candidate.achievementScore ?? "—")}/10</li>
        </ul>
      </span>
    </span>
  );
};

const SignalBadges: React.FC<{ c: CandidateProfile }> = ({ c }) => (
  <div className="flex flex-wrap gap-1">
    {c.profileVerified && <span title="Verified"><BadgeCheck size={13} className="text-indigo-500" /></span>}
    {c.profileComplete && <span title="Complete profile"><CheckCircle2 size={13} className="text-emerald-500" /></span>}
    {c.availabilityStatus && (
      <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 text-[9px] font-bold">{c.availabilityStatus}</span>
    )}
    {c.profileCompleteScore !== null && c.profileCompleteScore !== undefined && (
      <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 text-[9px] font-bold">
        {Math.round(Number(c.profileCompleteScore))}%
      </span>
    )}
  </div>
);

const Cell = ({ children }: { children: React.ReactNode }) => (
  <td className="px-3 py-2.5 align-top">{children}</td>
);

const ExpandedRow: React.FC<{ c: CandidateProfile }> = ({ c }) => (
  <tr className="bg-slate-50/80">
    <td colSpan={99} className="px-4 py-3">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
        <div>
          <p className="font-black text-slate-500 uppercase tracking-wide text-[9px] mb-1.5">Skills</p>
          <div className="flex flex-wrap gap-1">
            {c.technologyStack?.slice(0, 8).map((s) => (
              <span key={s} className="px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-700 font-semibold">{s}</span>
            ))}
            {c.coreExpertise?.slice(0, 4).map((s) => (
              <span key={s} className="px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 font-semibold">{s}</span>
            ))}
            {(!c.technologyStack?.length && !c.coreExpertise?.length) && <span className="text-slate-400">—</span>}
          </div>
        </div>
        <div>
          <p className="font-black text-slate-500 uppercase tracking-wide text-[9px] mb-1.5">Business Impact</p>
          <div className="flex flex-wrap gap-1">
            {c.businessImpactType?.slice(0, 5).map((s) => (
              <span key={s} className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold">{s}</span>
            ))}
            {(!c.businessImpactType?.length) && <span className="text-slate-400">—</span>}
          </div>
        </div>
        <div>
          <p className="font-black text-slate-500 uppercase tracking-wide text-[9px] mb-1.5">Top Achievements</p>
          <ul className="space-y-0.5 text-slate-600">
            {(c.topAchievements?.slice(0, 3) || []).map((a, i) => <li key={i}>• {a}</li>)}
            {!c.topAchievements?.length && <li className="text-slate-400">—</li>}
          </ul>
        </div>
        <div>
          <p className="font-black text-slate-500 uppercase tracking-wide text-[9px] mb-1.5">Companies</p>
          <div className="space-y-0.5 text-slate-600">
            {c.currentCompany && <p><b>Current:</b> {c.currentCompany}</p>}
            {c.notableCompanies?.length > 0 && <p><b>Previously:</b> {c.notableCompanies.join(", ")}</p>}
            {!c.currentCompany && !c.notableCompanies?.length && <p className="text-slate-400">—</p>}
          </div>
        </div>
      </div>
    </td>
  </tr>
);

export const CandidateResultTable: React.FC<CandidateResultTableProps> = ({
  results,
  totalElements,
  page,
  totalPages,
  loading,
  onPageChange,
  columns,
  onColumnsChange,
  onOpenDetails,
}) => {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const hasColumn = (key: string) => columns.includes(key);

  const toggleColumn = (key: string) => {
    const active = columns.includes(key);
    onColumnsChange(active ? columns.filter((c) => c !== key) : [...columns, key]);
  };

  const rows = results?.length ? results : [];

  return (
    <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
      {/* header + column config */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
        <div>
          <h3 className="text-sm font-black text-slate-800 flex items-center gap-2">
            <Users size={15} className="text-indigo-500" />
            Search Results
          </h3>
          <p className="text-[11px] text-slate-400 font-medium">
            {totalElements.toLocaleString()} candidates found
            {loading && <span className="ml-1 text-indigo-500">· searching…</span>}
          </p>
        </div>
        <div className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-bold text-slate-600 hover:border-indigo-200 hover:bg-indigo-50 flex items-center gap-1.5"
          >
            <Settings2 size={13} /> Columns <ChevronDown size={12} />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-full mt-1 z-20 w-56 bg-white border border-slate-200 rounded-xl shadow-xl p-2">
                <p className="px-2 py-1 text-[10px] font-black text-slate-400 uppercase tracking-wide">Additional columns</p>
                {OPTIONAL_RESULT_COLUMNS.map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 cursor-pointer text-xs text-slate-700">
                    <input type="checkbox" checked={hasColumn(key)} onChange={() => toggleColumn(key)} className="accent-indigo-600" />
                    {label}
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-left">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100 bg-slate-50/70">
              <th className="py-2.5 px-3 font-black">Candidate</th>
              <th className="py-2.5 px-3 font-black">Role</th>
              {hasColumn("experience") && <th className="py-2.5 px-3 font-black">Exp</th>}
              {hasColumn("leadership") && <th className="py-2.5 px-3 font-black">Leadership</th>}
              {hasColumn("team") && <th className="py-2.5 px-3 font-black">Team Size</th>}
              {hasColumn("industry") && <th className="py-2.5 px-3 font-black">Industry</th>}
              {hasColumn("location") && <th className="py-2.5 px-3 font-black">Location</th>}
              {hasColumn("signals") && <th className="py-2.5 px-3 font-black">Signals</th>}
              {hasColumn("skills") && <th className="py-2.5 px-3 font-black">Skills</th>}
              {hasColumn("currentCompany") && <th className="py-2.5 px-3 font-black">Company</th>}
              {hasColumn("previousCompanies") && <th className="py-2.5 px-3 font-black">Previous</th>}
              {hasColumn("businessImpact") && <th className="py-2.5 px-3 font-black">Impact</th>}
              {hasColumn("availability") && <th className="py-2.5 px-3 font-black">Availability</th>}
              {hasColumn("opportunityStatus") && <th className="py-2.5 px-3 font-black">Status</th>}
              {hasColumn("responseBehavior") && <th className="py-2.5 px-3 font-black">Decision</th>}
              {hasColumn("profileCompletion") && <th className="py-2.5 px-3 font-black">Profile</th>}
              {hasColumn("verification") && <th className="py-2.5 px-3 font-black">Verified</th>}
              {hasColumn("lastActive") && <th className="py-2.5 px-3 font-black">Last Active</th>}
              {hasColumn("match") && <th className="py-2.5 px-3 font-black">Match</th>}
              <th className="py-2.5 px-3 font-black w-8"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <React.Fragment key={c.id}>
                <tr
                  className={`border-b border-slate-50 hover:bg-indigo-50/40 cursor-pointer transition-colors ${expanded === c.id ? "bg-indigo-50/40" : ""}`}
                  onClick={() => onOpenDetails?.(c)}
                  title="Click to view full profile"
                >
                  <Cell>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenDetails?.(c);
                      }}
                      className="flex items-center gap-2.5 text-left group/name"
                    >
                      <span className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white flex items-center justify-center text-[11px] font-black shrink-0">
                        {initials(c)}
                      </span>
                      <span>
                        <span className="flex items-center gap-1 text-sm font-bold text-slate-800 group-hover/name:text-indigo-700">
                          {c.firstName} {c.lastName}
                          <ExternalLink size={11} className="text-slate-300 group-hover/name:text-indigo-400" />
                        </span>
                        <span className="block text-[10px] text-slate-400">{c.professionalHeadline || c.userEmail}</span>
                      </span>
                    </button>
                  </Cell>
                  <Cell>
                    <span className="block text-xs font-bold text-slate-700">{c.currentRole || "—"}</span>
                    <span className="block text-[10px] text-slate-400">{c.roleLevel || ""}</span>
                  </Cell>
                  {hasColumn("experience") && (
                    <Cell><span className="text-sm font-black text-slate-700">{c.totalYearsOfExperience ?? "—"} yrs</span></Cell>
                  )}
                  {hasColumn("leadership") && (
                    <Cell>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600">
                        <Users size={12} className="text-fuchsia-500" />
                        {c.leadershipScore ? `${c.leadershipScore}/10` : "—"}
                      </span>
                    </Cell>
                  )}
                  {hasColumn("team") && (
                    <Cell><span className="text-sm font-black text-slate-700">{c.teamSizeLed ? `${c.teamSizeLed}+` : "—"}</span></Cell>
                  )}
                  {hasColumn("industry") && (
                    <Cell>
                      <div className="flex flex-wrap gap-1 max-w-[140px]">
                        {(c.domainExpertise || []).slice(0, 2).map((d) => (
                          <span key={d} className="px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 text-[10px] font-semibold">{d}</span>
                        ))}
                        {!c.domainExpertise?.length && <span className="text-slate-300 text-xs">—</span>}
                      </div>
                    </Cell>
                  )}
                  {hasColumn("location") && (
                    <Cell>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600">
                        <MapPin size={11} className="text-amber-500" /> {c.city || "—"}
                      </span>
                    </Cell>
                  )}
                  {hasColumn("signals") && <Cell><SignalBadges c={c} /></Cell>}
                  {hasColumn("skills") && (
                    <Cell>
                      <span className="text-[11px] text-slate-600 font-semibold">
                        {(c.technologyStack || []).slice(0, 4).join(", ") || "—"}
                      </span>
                    </Cell>
                  )}
                  {hasColumn("currentCompany") && (
                    <Cell>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600">
                        <Building2 size={11} className="text-sky-500" /> {c.currentCompany || "—"}
                      </span>
                    </Cell>
                  )}
                  {hasColumn("previousCompanies") && (
                    <Cell><span className="text-[11px] text-slate-500">{(c.notableCompanies || []).slice(0, 3).join(", ") || "—"}</span></Cell>
                  )}
                  {hasColumn("businessImpact") && (
                    <Cell>
                      <span className="text-[11px] text-slate-600 font-semibold">
                        {(c.businessImpactType || []).slice(0, 3).join(", ") || (c.topAchievements || []).slice(0, 2).join(", ") || "—"}
                      </span>
                    </Cell>
                  )}
                  {hasColumn("availability") && (
                    <Cell>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
                        <Clock size={11} /> {c.noticePeriodDays != null ? `${c.noticePeriodDays}d` : "—"}
                      </span>
                    </Cell>
                  )}
                  {hasColumn("opportunityStatus") && (
                    <Cell><span className="text-[11px] font-semibold text-slate-600">{c.availabilityStatus || "—"}</span></Cell>
                  )}
                  {hasColumn("responseBehavior") && (
                    <Cell><span className="text-[11px] font-semibold text-slate-600">{c.decisionSpeed || "—"}</span></Cell>
                  )}
                  {hasColumn("profileCompletion") && (
                    <Cell>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-12 h-1 rounded-full bg-slate-100 overflow-hidden">
                          <span className="block h-full bg-indigo-400" style={{ width: `${Math.min(100, Number(c.profileCompleteScore || 0))}%` }} />
                        </span>
                        <span className="text-[10px] font-bold text-slate-600">{Math.round(Number(c.profileCompleteScore || 0))}%</span>
                      </span>
                    </Cell>
                  )}
                  {hasColumn("verification") && (
                    <Cell>
                      <div className="flex gap-1">
                        {c.profileVerified && <BadgeCheck size={14} className="text-indigo-500" />}
                        {c.linkedinVerified && <BadgeCheck size={14} className="text-sky-500" />}
                        {c.workEmailVerified && <BadgeCheck size={14} className="text-emerald-500" />}
                        {!c.profileVerified && !c.linkedinVerified && !c.workEmailVerified && <span className="text-[10px] text-slate-300">—</span>}
                      </div>
                    </Cell>
                  )}
                  {hasColumn("lastActive") && (
                    <Cell><span className="text-[11px] text-slate-500">{formatDate(c.updatedAt)}</span></Cell>
                  )}
                  {hasColumn("match") && <Cell><MatchScorer candidate={c} /></Cell>}
                  <Cell>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpanded(expanded === c.id ? null : c.id);
                      }}
                      className="p-1 rounded-lg hover:bg-slate-100 text-slate-400"
                      aria-label="Quick preview"
                      title="Quick preview (full profile = click row)"
                    >
                      <ChevronDown size={14} className={`transition-transform ${expanded === c.id ? "rotate-180" : ""}`} />
                    </button>
                  </Cell>
                </tr>
                {expanded === c.id && <ExpandedRow c={c} />}
              </React.Fragment>
            ))}
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={99} className="py-12 text-center">
                  <Zap size={22} className="mx-auto mb-2 text-slate-300" />
                  <p className="text-sm font-semibold text-slate-400">No candidates match the current filters.</p>
                  <p className="text-xs text-slate-300 mt-1">Try removing a filter or widening your search.</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* pagination */}
      <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between">
        <p className="text-[11px] text-slate-400 font-medium">
          Page {page + 1} of {Math.max(1, totalPages)}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={page <= 0 || loading}
            onClick={() => onPageChange(page - 1)}
            className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-bold text-slate-600 hover:border-indigo-200 disabled:opacity-40 flex items-center gap-1"
          >
            <ChevronLeft size={13} /> Prev
          </button>
          <button
            type="button"
            disabled={page >= totalPages - 1 || loading}
            onClick={() => onPageChange(page + 1)}
            className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-bold text-slate-600 hover:border-indigo-200 disabled:opacity-40 flex items-center gap-1"
          >
            Next <ChevronRight size={13} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CandidateResultTable;